import * as React from 'react';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { connect, DispatchProp } from 'react-redux';
import { addLocaleData, IntlProvider } from 'react-intl';
import hu from 'react-intl/locale-data/hu';

import { ApplicationState } from './reducers';
import { StoreType } from './configureStore';
import { ReduxAction } from './actions';
import messages from './i18n';

import * as Pages from './pages';
import Path from './utils/Path';

import './App.css';
import { PrivateRoute } from './components';
import * as Queries from '@api/graphql/queries';
import GraphQLClient from '@api/graphql/GraphQLClient';
import { updateAccount } from '@actions/authActions';
import { me, AdminProductCategoryList } from '@api/graphql/types';
import { Permissions } from './utils/Auth';
import { updateProductCategoryList } from '@actions/productCategoryActions';
import { drawered } from './utils/Drawered';
import ProductCategory from 'modules/ProductCategory';
import PaymentMethod from 'modules/PaymentMethod';
import ShippingMethod from 'modules/ShippingMethod';
import Timetable from 'modules/Timetable';
import User from 'modules/User';
import AdminUser from 'modules/AdminUser';
import BannerPlace from 'modules/BannerPlace';
import Banner from 'modules/Banner';
import PromotionCategory from '@modules/PromotionCategory';
import Product from '@modules/Product';
import ProductAttribute from '@modules/ProductAttribute';
import Cms from '@modules/Cms';
import UrlAlias from '@modules/UrlAlias';
import Order from '@modules/Order';
import Dashboard from '@modules/Dashboard';
import Coupon from '@modules/Coupon';
import ProductListAd from "@modules/ProductListAd";
import Donation from "@modules/Donation";

addLocaleData([...hu]);

interface ReduxProps {
    locale: string;
    isLoggedIn: boolean;
}

type Props = {
    store: StoreType,
} & ReduxProps & DispatchProp;

interface State {
    isLoading: boolean;
}

class App extends React.Component<Props, State> {
    public static store: StoreType | null = null;

    public static dispatch(action: ReduxAction) {
        // @ts-ignore
        App.store.dispatch(action);
    }

    public static getState(): ApplicationState {
        // @ts-ignore
        return App.store.getState();
    }

    constructor(props: Props) {
        super(props);
        App.store = props.store;
        this.state = {
            isLoading: this.props.isLoggedIn,
        };
    }

    public componentDidMount() {
        if (this.props.isLoggedIn) {
            GraphQLClient.query<me>({ query: Queries.me }).then((account) => {
                if (account.data && account.data.me) {
                    this.props.dispatch(updateAccount(account.data.me));
                }
                GraphQLClient.query<AdminProductCategoryList>({
                    query: Queries.adminProductCategoryList,
                    variables: {parent_id: null},
                }).then((response) => {
                    if (response.data && response.data.adminProductCategories) {
                        this.props.dispatch(updateProductCategoryList(response.data.adminProductCategories));
                    }
                    this.setState({ isLoading: false });
                });
            }).catch(() => {
                this.setState({ isLoading: false });
            });
        }

        // Adding body-small to body for sidebar resizing
        ['DOMContentLoaded', 'resize'].forEach( (event) => {
            window.addEventListener(event, () => {
                (document.body.clientWidth < 769) ? document.body.classList.add("body-small") : document.body.classList.remove("body-small");
            });
        });
    }

    public render() {
        const { locale } = this.props;
        if (this.state.isLoading) {
            return null;
        }

        return (
            <IntlProvider locale={locale} messages={messages[locale]}>
                <BrowserRouter>
                    <Switch>
                        <Route exact={true} path={Path.login} component={Pages.Login} />
                        <PrivateRoute requireRole={[]} exact={true} path={Path.dashboard} component={drawered(Dashboard.pages.Dashboard)} />

                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={Path.productCategoryCreate} component={drawered(ProductCategory.pages.ProductCategoryCreate)} />
                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={`${Path.productCategory}/:productCategoryId`} component={drawered(ProductCategory.pages.ProductCategoryEdit)} />
                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={Path.productCategory} component={ProductCategory.pages.ProductCategoryList} />

                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={Path.promotionCategoryCreate} component={drawered(PromotionCategory.pages.PromotionCategoryCreate)} />
                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={`${Path.promotionCategory}/:promotionCategoryId`} component={drawered(PromotionCategory.pages.PromotionCategoryEdit)} />
                        <PrivateRoute requireRole={[Permissions.productCategories]} exact={true} path={Path.promotionCategory} component={PromotionCategory.pages.PromotionCategoryList} />

                        <PrivateRoute requireRole={[Permissions.shipping]} exact={true} path={`${Path.shippingMethod}/:shippingMethodId`} component={drawered(ShippingMethod.pages.ShippingMethodEdit)} />
                        <PrivateRoute requireRole={[Permissions.shipping]} exact={true} path={Path.shippingMethod} component={ShippingMethod.pages.ShippingMethodList} />

                        <PrivateRoute requireRole={[Permissions.payments]} exact={true} path={`${Path.paymentMethod}/:paymentMethodId`} component={drawered(PaymentMethod.pages.PaymentMethodEdit)} />
                        <PrivateRoute requireRole={[Permissions.payments]} exact={true} path={Path.paymentMethod} component={PaymentMethod.pages.PaymentMethodList} />

                        <PrivateRoute requireRole={[Permissions.users]} exact={true} path={`${Path.user}/:userId`} component={drawered(User.pages.UserEdit)} />
                        <PrivateRoute requireRole={[Permissions.users]} exact={true} path={`${Path.user}/:userId/:discountType${Path.discountsCreate}`} component={drawered(User.pages.UserDiscountCreate)} />
                        <PrivateRoute requireRole={[Permissions.users]} exact={true} path={`${Path.user}/:userId/:discountType/:discountId`} component={drawered(User.pages.UserDiscountEdit)} />
                        <PrivateRoute requireRole={[Permissions.users]} exact={true} path={Path.user} component={User.pages.UserList} />

                        <PrivateRoute requireRole={[Permissions.coupons]} exact={true} path={Path.couponCreate} component={drawered(Coupon.pages.CouponCreate)} />
                        <PrivateRoute requireRole={[Permissions.coupons]} exact={true} path={`${Path.coupon}/:id`} component={drawered(Coupon.pages.CouponEdit)} />
                        <PrivateRoute requireRole={[Permissions.coupons]} exact={true} path={Path.coupon} component={Coupon.pages.CouponList} />

                        <PrivateRoute requireRole={[Permissions.admins]} exact={true} path={Path.adminUserCreate} component={drawered(AdminUser.pages.AdminUserCreate)} />
                        <PrivateRoute requireRole={[Permissions.admins]} exact={true} path={`${Path.adminUser}/:id`} component={drawered(AdminUser.pages.AdminUserEdit)} />
                        <PrivateRoute requireRole={[Permissions.admins]} exact={true} path={Path.adminUser} component={AdminUser.pages.AdminUserList} />

                        <PrivateRoute requireRole={[Permissions.banners]} exact={true} path={Path.bannerPlace} component={BannerPlace.pages.BannerPlaceList} />

                        <PrivateRoute requireRole={[Permissions.banners]} exact={true} path={Path.bannerCreate} component={drawered(Banner.pages.BannerCreate)} />
                        <PrivateRoute requireRole={[Permissions.banners]} exact={true} path={`${Path.banner}/:bannerId`} component={drawered(Banner.pages.BannerEdit)} />
                        <PrivateRoute requireRole={[Permissions.banners]} exact={true} path={Path.banner} component={Banner.pages.BannerList} />

                        <PrivateRoute requireRole={[Permissions.productListAds]} exact={true} path={Path.productListAd} component={ProductListAd.pages.ProductListAdList} />
                        <PrivateRoute requireRole={[Permissions.productListAds]} exact={true} path={Path.productListAdCreate} component={drawered(ProductListAd.pages.ProductListAdCreate)} />
                        <PrivateRoute requireRole={[Permissions.productListAds]} exact={true} path={`${Path.productListAd}/:id`} component={drawered(ProductListAd.pages.ProductListAdEdit)} />

                        <PrivateRoute requireRole={[Permissions.timetables]} exact={true} path={Path.timetable} component={Timetable.pages.TimetableList} />

                        <PrivateRoute requireRole={[Permissions.products]} exact={true} path={`${Path.product}/:id`} component={drawered(Product.pages.ProductEdit)} />
                        <PrivateRoute requireRole={[Permissions.products]} exact={true} path={Path.product} component={Product.pages.ProductList} />

                        <PrivateRoute requireRole={[Permissions.donations]} exact={true} path={Path.donationCreate} component={drawered(Donation.pages.DonationCreate)} />
                        <PrivateRoute requireRole={[Permissions.donations]} exact={true} path={`${Path.donation}/:id`} component={drawered(Donation.pages.DonationEdit)} />
                        <PrivateRoute requireRole={[Permissions.donations]} exact={true} path={Path.donation} component={Donation.pages.DonationList} />

                        <PrivateRoute requireRole={[Permissions.productAttributes]} exact={true} path={Path.productAttributeCreate} component={drawered(ProductAttribute.pages.ProductAttributeCreate)} />
                        <PrivateRoute requireRole={[Permissions.productAttributes]} exact={true} path={`${Path.productAttribute}/:id`} component={drawered(ProductAttribute.pages.ProductAttributeEdit)} />
                        <PrivateRoute requireRole={[Permissions.productAttributes]} exact={true} path={Path.productAttribute} component={ProductAttribute.pages.ProductAttributeList} />

                        <PrivateRoute requireRole={[Permissions.cms]} exact={true} path={Path.cmsCreate} component={drawered(Cms.pages.CmsCreate)} />
                        <PrivateRoute requireRole={[Permissions.cms]} exact={true} path={`${Path.cms}/:cmsId`} component={drawered(Cms.pages.CmsEdit)} />
                        <PrivateRoute requireRole={[Permissions.cms]} exact={true} path={Path.cms} component={Cms.pages.CmsList} />

                        <PrivateRoute requireRole={[Permissions.urlAliases]} exact={true} path={Path.urlAliasCreate} component={drawered(UrlAlias.pages.UrlAliasCreate)} />
                        <PrivateRoute requireRole={[Permissions.urlAliases]} exact={true} path={`${Path.urlAlias}/:id`} component={drawered(UrlAlias.pages.UrlAliasEdit)} />
                        <PrivateRoute requireRole={[Permissions.urlAliases]} exact={true} path={Path.urlAlias} component={UrlAlias.pages.UrlAliasList} />

                        <PrivateRoute requireRole={[Permissions.orders]} exact={true} path={`${Path.order}/:id`} component={drawered(Order.pages.OrderDisplay)} />
                        <PrivateRoute requireRole={[Permissions.orders]} exact={true} path={Path.order} component={Order.pages.OrderList} />

                        <Redirect exact={true} from={Path.shipping} to={Path.shippingMethod} />
                        <Redirect exact={true} from={Path.users} to={Path.user} />
                        <Redirect exact={true} from={Path.banners} to={Path.banner} />
                        <Redirect exact={true} from={Path.products} to={Path.product} />

                        <Redirect exact={true} from={Path.root} to={Path.login} />
                        <Route path={'*'} component={drawered(Pages.PageNotFound)} />
                    </Switch>
                </BrowserRouter>
            </IntlProvider>
        );
    }
}

const mapStateToProps = ({ auth }: ApplicationState): ReduxProps => {
    const isLoggedIn = !!auth.user;
    return {
        locale: 'hu',
        isLoggedIn,
    };
};

export default connect(mapStateToProps)(App);



// WEBPACK FOOTER //
// ./src/App.tsx